import React, { useState, useEffect, useContext } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Users, 
  DollarSign, 
  TrendingUp, 
  Calculator,
  ArrowLeft,
  Crown,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  FileSpreadsheet
} from "lucide-react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    
    fetchAdminData();
  }, [user, navigate]);

  const fetchAdminData = async () => {
    try {
      const [statsResponse, usersResponse, transactionsResponse] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/transactions`)
      ]);

      setStats(statsResponse.data);
      setUsers(usersResponse.data);
      setTransactions(transactionsResponse.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const value = amount || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { variant: "default", icon: <CheckCircle className="w-3 h-3" />, color: "text-green-700" },
      inactive: { variant: "secondary", icon: <XCircle className="w-3 h-3" />, color: "text-gray-700" },
      cancelled: { variant: "destructive", icon: <XCircle className="w-3 h-3" />, color: "text-red-700" }
    };

    const config = statusConfig[status] || statusConfig.inactive;
    
    return (
      <Badge variant={config.variant} className={`${config.color} flex items-center gap-1`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      completed: { variant: "default", icon: <CheckCircle className="w-3 h-3" />, color: "text-green-700" },
      pending: { variant: "secondary", icon: <Clock className="w-3 h-3" />, color: "text-yellow-700" },
      failed: { variant: "destructive", icon: <XCircle className="w-3 h-3" />, color: "text-red-700" },
      cancelled: { variant: "outline", icon: <XCircle className="w-3 h-3" />, color: "text-gray-700" }
    };

    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Badge variant={config.variant} className={`${config.color} flex items-center gap-1`}>
        {config.icon}
        {status}
      </Badge>
    );
  };

  if (!user?.is_admin) {
    return null;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Admin Panel
              </span>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/dashboard')}
                className="text-slate-600 hover:text-slate-800"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              
              <div className="text-sm text-slate-600">
                {user?.username} • Administrator
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-slate-800 mb-4" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Tax Helper Pros Admin
          </h1>
          <p className="text-xl text-slate-600">
            Manage users, subscriptions, and monitor platform performance
          </p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {stats.total_users || 0}
                </div>
                <p className="text-xs text-muted-foreground">Registered accounts</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active Subscribers</CardTitle>
                <Crown className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {stats.active_subscribers || 0}
                </div>
                <p className="text-xs text-muted-foreground">Paying customers</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-purple-600">
                  {formatCurrency(stats.total_revenue || 0)}
                </div>
                <p className="text-xs text-muted-foreground">All-time earnings</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {(stats.conversion_rate || 0).toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">Trial to paid</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Detailed Data */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="users">Users Management</TabsTrigger>
            <TabsTrigger value="transactions">Payment Transactions</TabsTrigger>
          </TabsList>

          <TabsContent value="users" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  All Users ({users.length})
                </CardTitle>
                <CardDescription>
                  Manage user accounts and subscription status
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="space-y-3">
                    {users.map((user) => (
                      <div key={user.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg bg-white">
                        <div className="flex-1 mb-2 md:mb-0">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium text-slate-800">{user.username}</p>
                              <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                            {user.is_admin && (
                              <Badge variant="outline" className="text-purple-700 border-purple-200">
                                <Shield className="w-3 h-3 mr-1" />
                                Admin
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Joined: {formatDate(user.created_date)}
                          </p>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                          <div className="text-center">
                            {getStatusBadge(user.subscription_status)}
                            {user.subscription_plan && (
                              <p className="text-xs text-slate-500 mt-1">
                                {user.subscription_plan} plan
                              </p>
                            )}
                          </div>
                          
                          {user.subscription_end_date && (
                            <div className="text-xs text-slate-500">
                              Expires: {formatDate(user.subscription_end_date)}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {users.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No users found</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="transactions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  Payment Transactions ({transactions.length})
                </CardTitle>
                <CardDescription>
                  Monitor all payment transactions and subscription changes
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <div className="space-y-3">
                    {transactions.map((transaction) => (
                      <div key={transaction.id} className="flex flex-col md:flex-row md:items-center justify-between p-4 border rounded-lg bg-white">
                        <div className="flex-1 mb-2 md:mb-0">
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="font-medium text-slate-800">{transaction.email}</p>
                              <p className="text-sm text-slate-500">
                                {formatCurrency(transaction.amount)} • {transaction.plan_type} plan
                              </p>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            Created: {formatDate(transaction.created_date)}
                            {transaction.updated_date && ` • Updated: ${formatDate(transaction.updated_date)}`}
                          </p>
                        </div>
                        
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
                          {getPaymentStatusBadge(transaction.payment_status)}
                          
                          <div className="text-xs text-slate-500">
                            Session: ...{transaction.session_id.slice(-8)}
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {transactions.length === 0 && (
                      <div className="text-center py-12 text-slate-500">
                        <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No transactions found</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Actions */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
            <CardDescription>
              Common administrative tasks and tools
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                onClick={() => fetchAdminData()}
                className="flex items-center justify-center gap-2 h-12"
              >
                <TrendingUp className="w-4 h-4" />
                Refresh Data
              </Button>
              
              <Button
                variant="outline"
                onClick={() => toast.info("Export functionality coming soon")}
                className="flex items-center justify-center gap-2 h-12"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export Users
              </Button>
              
              <Button
                variant="outline"
                onClick={() => toast.info("Analytics dashboard coming soon")}
                className="flex items-center justify-center gap-2 h-12"
              >
                <Calculator className="w-4 h-4" />
                View Analytics
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;